import React, { useState, useContext, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { Button, SelectSearchQuery } from '@tymate/elise';
import { Container } from '@tymate/elise/ui';
import { FormLabel as RawFormLabel } from '@tymate/elise/ui/forms';
import { DatePicker, Modal } from '@tymate/elise/components';
import { Stack } from '@tymate/margaret';
import { CollectionPointContext } from 'contexts';
import { useReporting } from 'contexts/ReportingContext';
import CollectionPointsTreeWrapper from 'components/CollectionPointsTree';
import { startOfDay, endOfDay, isValid } from 'date-fns';
import { debounce } from 'lodash';
import { getFranchiseCollectionPoints } from 'api/collectionPoints';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';

const FormLabel = styled(RawFormLabel)`
  width: auto;
  margin-bottom: 0;
`;

const StateTag = styled.span`
  width: max-content;
  background-color: ${({ theme }) => theme.primaryLighten};
  font-size: 12px;
  color: ${({ theme }) => theme.primary};
  border-radius: 8px;
  padding-right: ${({ theme }) => theme.spacing()};
  padding-left: ${({ theme }) => theme.spacing()};
  line-height: 24px;
  height: 24px;
`;

const PortalDatePicker = ({
  value,
  onChange,
  minDate,
  modalOpen,
  ...props
}) => {
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const elementRef = useRef(null);

  useEffect(() => {
    const updatePosition = () => {
      if (elementRef.current) {
        const rect = elementRef.current.getBoundingClientRect();
        setPosition({
          top: rect.top + window.scrollY,
          left: rect.left + window.scrollX,
        });
      }
    };

    // Initial position
    updatePosition();

    // Update position on scroll and resize
    window.addEventListener('scroll', updatePosition);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition);
      window.removeEventListener('resize', updatePosition);
    };
  }, []);

  return (
    <>
      <div
        ref={elementRef}
        style={{
          width: '142px',
          height: '36px',
          display: 'flex',
          alignItems: 'center',
        }}
      />
      {!modalOpen &&
        createPortal(
          <div
            style={{
              position: 'absolute',
              top: position.top,
              left: position.left,
              zIndex: 9999,
            }}
          >
            <DatePicker
              value={value}
              onChange={onChange}
              minDate={minDate}
              {...props}
            />
          </div>,
          document.body,
        )}
    </>
  );
};

PortalDatePicker.propTypes = {
  value: PropTypes.instanceOf(Date),
  onChange: PropTypes.func.isRequired,
  minDate: PropTypes.instanceOf(Date),
  modalOpen: PropTypes.bool,
};

const PortalSelectSearchQuery = ({
  queryKey,
  queryFn,
  searchKey,
  initialValueLabel,
  labelKey,
  labelValue,
  hasFixedWidth,
  onChange,
  onSuccess,
  disabled,
  modalOpen,
  ...props
}) => {
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const elementRef = useRef(null);

  useEffect(() => {
    const updatePosition = () => {
      if (elementRef.current) {
        const rect = elementRef.current.getBoundingClientRect();
        setPosition({
          top: rect.top + window.scrollY,
          left: rect.left + window.scrollX,
        });
      }
    };

    // Initial position
    updatePosition();

    // Update position on scroll and resize
    window.addEventListener('scroll', updatePosition);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition);
      window.removeEventListener('resize', updatePosition);
    };
  }, []);

  return (
    <>
      <div
        ref={elementRef}
        style={{
          width: '142px',
          height: '36px',
          display: 'flex',
          alignItems: 'center',
        }}
      />
      {!modalOpen &&
        createPortal(
          <div
            style={{
              position: 'absolute',
              top: position.top,
              left: position.left,
              zIndex: 9998,
            }}
          >
            <SelectSearchQuery
              queryKey={queryKey}
              queryFn={queryFn}
              searchKey={searchKey}
              initialValueLabel={initialValueLabel}
              labelKey={labelKey}
              labelValue={labelValue}
              hasFixedWidth={hasFixedWidth}
              onChange={onChange}
              onSuccess={onSuccess}
              disabled={disabled}
              {...props}
            />
          </div>,
          document.body,
        )}
    </>
  );
};

PortalSelectSearchQuery.propTypes = {
  queryKey: PropTypes.array.isRequired,
  queryFn: PropTypes.func.isRequired,
  searchKey: PropTypes.string.isRequired,
  initialValueLabel: PropTypes.string,
  labelKey: PropTypes.string.isRequired,
  labelValue: PropTypes.string.isRequired,
  hasFixedWidth: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  disabled: PropTypes.bool,
  modalOpen: PropTypes.bool,
};

const ReportingParams = ({ showCollectionPointSelect = true }) => {
  const { collectionPoint, selectCollectionPoint } = useContext(
    CollectionPointContext,
  );
  const { reportingParams, updateReportingParams } = useReporting();
  const [startDateIsUpdated, setStartDateIsUpdated] = useState(false);
  const [
    collectionPointsSelectModalIsShown,
    setCollectionPointsSelectModalIsShown,
  ] = useState(false);
  const [tempParams, setTempParams] = useState(null);
  const [collectionPoints, setCollectionPoints] = useState([]);

  const handleCollectionPointChange = ({ value }) => {
    if (collectionPoint?.id !== value) {
      selectCollectionPoint({ id: value });
    }
  };

  const handleCollectionPointsLoad = data => {
    setCollectionPoints(data);
  };

  const debouncedStartDateChange = debounce(date => {
    if (isValid(date)) {
      updateReportingParams({ startAt: startOfDay(date) });
      setStartDateIsUpdated(true);
    } else {
      throw new Error('Invalid date on start date change');
    }
  }, 800);

  const handleStartDateChange = date => {
    debouncedStartDateChange(date);
  };

  const debouncedEndDateChange = debounce(date => {
    if (isValid(date)) {
      updateReportingParams({ 
        endAt: endOfDay(date),
        endAtManuallyChanged: true 
      });
    } else {
      throw new Error('Invalid date on end date change');
    }
  }, 800);

  const handleEndDateChange = date => {
    debouncedEndDateChange(endOfDay(date));
  };

  const handleOpenCollectionPointsModal = () => {
    const params = {
      collectionPointIds: reportingParams.collectionPointIds,
      additionalCollectionPointsIds: reportingParams.additionalCollectionPointsIds,
      closestCommonParent: reportingParams.closestCommonParent,
    };
    setTempParams(params);
    setCollectionPointsSelectModalIsShown(true);
  };

  return (
    <Container>
      <Stack direction="column" gutterSize={2}>
        <Stack alignY="center" gutterSize={1}>
          <Stack
            direction={{ default: 'column', tablet: 'row' }}
            alignY="center"
            gutterSize={0.5}
          >
            <FormLabel>Du</FormLabel>
            {/* Do not use the PortalDatePicker on the front page */}
            {showCollectionPointSelect && (
              <PortalDatePicker
                value={reportingParams.startAt}
                onChange={handleStartDateChange}
                modalOpen={collectionPointsSelectModalIsShown}
              />
            )}
            {!showCollectionPointSelect && (
              <DatePicker
                value={reportingParams.startAt}
                onChange={handleStartDateChange}
              />
            )}
          </Stack>

          <Stack
            direction={{ default: 'column', tablet: 'row' }}
            alignY="center"
            gutterSize={0.5}
          >
            <FormLabel>au</FormLabel>
            {/* Do not use the PortalDatePicker on the front page */}
            {showCollectionPointSelect && (
              <PortalDatePicker
                value={reportingParams.endAt}
                minDate={!startDateIsUpdated && reportingParams.startAt}
                onChange={handleEndDateChange}
                modalOpen={collectionPointsSelectModalIsShown}
              />
            )}
            {!showCollectionPointSelect && (
              <DatePicker
                value={reportingParams.endAt}
                minDate={!startDateIsUpdated && reportingParams.startAt}
                onChange={handleEndDateChange}
              />
            )}
          </Stack>
        </Stack>
        {showCollectionPointSelect && (
          <PortalSelectSearchQuery
            queryKey={['franchiseCollectionPoints']}
            queryFn={getFranchiseCollectionPoints}
            searchKey="filter[search]"
            initialValueLabel={collectionPoint?.name}
            labelKey="name"
            labelValue="id"
            hasFixedWidth
            onChange={handleCollectionPointChange}
            onSuccess={handleCollectionPointsLoad}
            disabled={collectionPoints.length <= 1}
            modalOpen={collectionPointsSelectModalIsShown}
          />
        )}
        <Stack
          gutterSize={1}
          alignY="center"
          direction={{ default: 'column', tablet: 'row' }}
        >
          <Button variant="primary" onClick={handleOpenCollectionPointsModal}>
            Sélectionner les points de collecte à afficher
          </Button>
          {reportingParams.collectionPointIds.length > 0 && (
            <StateTag>
              {reportingParams.collectionPointIds.length +
                reportingParams.additionalCollectionPointsIds.length}{' '}
              {reportingParams.collectionPointIds.length +
                reportingParams.additionalCollectionPointsIds.length >
              1
                ? 'points de collecte sélectionnés'
                : 'point de collecte sélectionné'}
            </StateTag>
          )}
        </Stack>
      </Stack>

      {collectionPointsSelectModalIsShown && (
        <Modal
          isOpen
          variant="medium"
          title="Sélectionnez les points de collecte"
          onRequestClose={() => setCollectionPointsSelectModalIsShown(false)}
        >
          <CollectionPointsTreeWrapper
            collectionPoint={collectionPoint}
            defaultSelectedSelectionPointIds={tempParams}
            onChange={({
              collectionPointIds,
              additionalCollectionPointsIds,
              closestCommonParent,
            }) => {
              const newTempParams = {
                collectionPointIds,
                additionalCollectionPointsIds,
                closestCommonParent,
              };
              setTempParams(newTempParams);
            }}
          />
          <Stack direction="row" gutterSize={1} alignX="center" marginTop={2}>
            <Button
              variant="primary"
              onClick={() => {
                const newParams = {
                  ...reportingParams,
                  collectionPointIds: tempParams.collectionPointIds || [],
                  additionalCollectionPointsIds: tempParams.additionalCollectionPointsIds || [],
                  closestCommonParent: tempParams.closestCommonParent || null,
                };
                updateReportingParams(newParams);
                setTempParams(null);
                setCollectionPointsSelectModalIsShown(false);
              }}
            >
              Valider
            </Button>
          </Stack>
        </Modal>
      )}
    </Container>
  );
};

ReportingParams.propTypes = {
  showCollectionPointSelect: PropTypes.bool,
};

export default ReportingParams;
